import imageCompression from "browser-image-compression";

export function capitalizeFirstLetter(str) {
  return str?.charAt(0).toUpperCase() + str.slice(1);
}

export function getFirstError(errors) {
  if (typeof errors !== "object" || errors === null) {
    return "Something went wrong";
  }
  for (const key in errors) {
    if (Array.isArray(errors[key])) {
      return `${capitalizeFirstLetter(key)}: ${capitalizeFirstLetter(errors[key][0])}`;
    } else if (typeof errors[key] === "object" && errors[key] !== null) {
      const innerError = getFirstError(errors[key]);
      if (innerError) {
        return `${capitalizeFirstLetter(key)}: ${capitalizeFirstLetter(innerError)}`;
      }
    } else return `${capitalizeFirstLetter(errors[key])}`;
  }
  return null;
}

export function formatText(text) {
  return text.split(/(\*\*[^*]+\*\*)/).map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <span key={index} className="font-bold">
          {part.slice(2, -2)}
        </span>
      );
    }
    return part;
  });
}

export async function compressImage(file) {
  const options = {
    maxSizeMB: 1,
    maxWidthOrHeight: 1920,
    useWebWorker: true,
  };

  try {
    if (file.size <= 1024 * 1024) {
      return file;
    }

    const compressedFile = await imageCompression(file, options);

    // Create a new File object with the original name and type
    return new File([compressedFile], file.name, { type: file.type });
  } catch (error) {
    console.error("Error compressing image:", error);
    throw new Error("Failed to compress image");
  }
}

export async function handleFileUpload(event, deps) {
  const {
    setIsLoading,
    setSelectedImage,
    setImagePreview,
    toast,
  } = deps;
  const file = event.target.files[0];
  if (file) {
    const allowedExtensions = [
      "bmp",
      "dib",
      "gif",
      "jfif",
      "jpe",
      "jpg",
      "jpeg",
      "pbm",
      "pgm",
      "ppm",
      "pnm",
      "pfm",
      "png",
      "apng",
      "blp",
      "bufr",
      "cur",
      "pcx",
      "dcx",
      "dds",
      "ps",
      "eps",
      "fit",
      "fits",
      "fli",
      "flc",
      "ftc",
      "ftu",
      "gbr",
      "grib",
      "h5",
      "hdf",
      "jp2",
      "j2k",
      "jpc",
      "jpf",
      "jpx",
      "j2c",
      "icns",
      "ico",
      "im",
      "iim",
      "mpg",
      "mpeg",
      "tif",
      "tiff",
      "mpo",
      "msp",
      "palm",
      "pcd",
      "pdf",
      "pxr",
      "psd",
      "qoi",
      "bw",
      "rgb",
      "rgba",
      "sgi",
      "ras",
      "tga",
      "icb",
      "vda",
      "vst",
      "webp",
      "wmf",
      "emf",
      "xbm",
      "xpm",
    ];
    const fileExtension = file.name.split(".").pop().toLowerCase();

    if (!allowedExtensions.includes(fileExtension)) {
      toast.error(
        `File type .${fileExtension} is not allowed. Please use one of the following formats: jpg, jpeg, png, gif, etc.`
      );
      event.target.value = ""; 
      return;
    }

    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      event.target.value = "";
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image size should be less than 10MB");
      event.target.value = "";
      return;
    }

    try {
      setIsLoading(true);
      const compressedImage = await compressImage(file);
      setSelectedImage(compressedImage);
      setImagePreview(URL.createObjectURL(compressedImage));
    } catch (error) {
      toast.error("Failed to process image. Please try a different image.");
    } finally {
      setIsLoading(false);
      event.target.value = "";
    }
  }
}

export async function uploadToS3(file, postUploadImage) {
  const formData = new FormData();
  formData.append("image", file);

  try {
    const data = await postUploadImage({
      body: formData,
    }).unwrap();

    if (!data?.success) {
      throw data;
    }

    return data.image_url;
  } catch (error) {
    console.error("Error uploading image:", error);
    throw error;
  }
}

/**
 * Converts feet and inches to centimeters
 * @param {string} feet - Height in feet
 * @param {string} inches - Additional inches
 * @returns {number} Total height in centimeters (rounded)
 */
export function convertFeetInchesToCm(feet, inches) {
  const totalInches = (parseFloat(feet) || 0) * 12 + (parseFloat(inches) || 0);
  return Math.round(totalInches * 2.54);
}

/**
 * Converts centimeters to feet and inches
 * @param {string} cm - Height in centimeters
 * @returns {object} Object with feet and inches as strings
 */
export function convertCmToFeetInches(cm) {
  const totalInches = (parseFloat(cm) || 0) / 2.54;
  const feet = Math.floor(totalInches / 12);
  const inches = Math.round(totalInches % 12);
  return { feet: feet.toString(), inches: inches.toString() };
}

/**
 * Converts pounds to kilograms
 * @param {string} lbs - Weight in pounds
 * @returns {number} Weight in kilograms (rounded)
 */
export function convertLbsToKg(lbs) {
  return Math.round((parseFloat(lbs) || 0) * 0.453592);
}

/**
 * Converts kilograms to pounds
 * @param {string} kg - Weight in kilograms
 * @returns {number} Weight in pounds (rounded)
 */
export function convertKgToLbs(kg) {
  return Math.round((parseFloat(kg) || 0) / 0.453592);
}

/**
 * Converts inches to centimeters
 * @param {string} inches - Measurement in inches
 * @returns {number} Measurement in centimeters (rounded)
 */
export function convertInchesToCm(inches) {
  return Math.round((parseFloat(inches) || 0) * 2.54);
}

/**
 * Converts centimeters to inches
 * @param {string} cm - Measurement in centimeters
 * @returns {number} Measurement in inches (rounded)
 */
export function convertCmToInches(cm) {
  return Math.round((parseFloat(cm) || 0) / 2.54);
}

/**
 * Generic function to convert measurements from imperial to metric
 * @param {object} measurements - Current measurements object
 * @param {function} setMeasurements - State setter function
 * @param {array} fieldsToConvert - Array of field configurations to convert
 */
export function convertImperialToMetric(measurements, setMeasurements, fieldsToConvert = []) {
  const conversions = {};

  fieldsToConvert.forEach(field => {
    switch (field.type) {
      case 'height':
        if (measurements.feet || measurements.inches) {
          conversions.height = convertFeetInchesToCm(measurements.feet, measurements.inches).toString();
        } else {
          conversions.height = "";
        }
        conversions.feet = ""; // Clear feet for metric mode
        conversions.inches = ""; // Clear inches for metric mode
        break;
      
      case 'weight':
        conversions.weight = measurements.weight ? convertLbsToKg(measurements.weight).toString() : "";
        break;
      
      case 'waist':
        conversions.waist_size = measurements.waist_size ? convertInchesToCm(measurements.waist_size).toString() : "";
        break;
      
      case 'inseam':
        conversions.inseam = measurements.inseam ? convertInchesToCm(measurements.inseam).toString() : "";
        break;
    }
  });

  setMeasurements({
    ...measurements,
    ...conversions,
  });
}

/**
 * Generic function to convert measurements from metric to imperial
 * @param {object} measurements - Current measurements object
 * @param {function} setMeasurements - State setter function
 * @param {array} fieldsToConvert - Array of field configurations to convert
 */
export function convertMetricToImperial(measurements, setMeasurements, fieldsToConvert = []) {
  const conversions = {};

  fieldsToConvert.forEach(field => {
    switch (field.type) {
      case 'height':
        if (measurements.height) {
          const { feet, inches } = convertCmToFeetInches(measurements.height);
          conversions.feet = feet;
          conversions.inches = inches;
        } else {
          conversions.feet = "";
          conversions.inches = "";
        }
        conversions.height = ""; // Clear height for imperial mode
        break;
      
      case 'weight':
        conversions.weight = measurements.weight ? convertKgToLbs(measurements.weight).toString() : "";
        break;
      
      case 'waist':
        conversions.waist_size = measurements.waist_size ? convertCmToInches(measurements.waist_size).toString() : "";
        break;
      
      case 'inseam':
        conversions.inseam = measurements.inseam ? convertCmToInches(measurements.inseam).toString() : "";
        break;
    }
  });

  setMeasurements({
    ...measurements,
    ...conversions,
  });
}

export const toPriceInt = (rawPrice) => {
  if (rawPrice == null) return null;
  const s = String(rawPrice).trim();

  const m = s.match(/^\s*([^0-9.,+\-]+)?\s*(.*)$/);
  const currency = (m?.[1] ?? "").trim();
  const rest = (m?.[2] ?? "").trim();

  const clean = rest.replace(/[^\d.,]/g, "");
  if (!clean) return s;

  const [intRaw, decRaw = ""] = clean.split(".");
  const intDigits = (intRaw || "").replace(/,/g, "");
  if (!intDigits) return `${currency}${clean}`;

  const intWithCommas = intDigits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const numberFormatted = decRaw === "" || decRaw === "00" ? intWithCommas : `${intWithCommas}.${decRaw}`;

  return `${currency}${numberFormatted}`;
};

export const normalizeCurrency = (str) => {
  if (typeof str !== "string") return str;
  return str.replace(/^(\$)\s+/, "$1");
}

export const cx = (...xs) => xs.filter(Boolean).join(" ");

export const isLikelyBadImageUrl = (url) => {
  if (!url || typeof url !== "string") return true;

  const normalized = url.trim().toLowerCase();

  if (!normalized) return true;
  if (normalized.includes("/null")) return true;
  if (normalized.endsWith("null")) return true;

  if (
    normalized.includes("/api/public/products/") &&
    !(
      normalized.endsWith(".jpg") ||
      normalized.endsWith(".jpeg") ||
      normalized.endsWith(".png") ||
      normalized.endsWith(".webp") ||
      normalized.endsWith(".gif")
    )
  ) {
    return true;
  }

  return false;
};

export const normalizeImageUrl = (url) => {
  if (!url || typeof url !== "string") return "";

  const trimmed = url.trim();
  if (!trimmed) return "";

  if (trimmed.startsWith("http://admin.eveness.ai")) {
    return trimmed.replace("http://", "https://");
  }

  return trimmed;
};

const toImageArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value) return [value];
  return [];
};

export const getSafeImageList = (images) => {
  return Array.from(
    new Set(
      toImageArray(images)
        .map((img) => normalizeImageUrl(img))
        .filter((img) => !isLikelyBadImageUrl(img))
    )
  );
};

export const getSafeVariantImage = (variant) => {
  const normalized = normalizeImageUrl(variant?.image);
  return isLikelyBadImageUrl(normalized) ? "" : normalized;
};

export const getRecommendedCardImage = (item) => {
  const candidates = [
    item?.image,
    ...(Array.isArray(item?.main_images) ? item.main_images : item?.main_images ? [item.main_images] : []),
  ];

  for (const candidate of candidates) {
    const normalized = normalizeImageUrl(candidate);
    if (!isLikelyBadImageUrl(normalized)) {
      return normalized;
    }
  }

  return "";
};

export const getVisibleCardMatchPercentage = (item) => {
  const value =
    item?.matchPercentage ??
    item?.match_percentage ??
    item?.product_score ??
    null;

  if (value === null || value === undefined || value === "") {
    return null;
  }

  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : null;
};

export const formatProductPrice = (price) => {
  if (price === null || price === undefined || price === "") {
    return "";
  }

  const trimmedPrice = String(price).trim();

  if (!trimmedPrice) {
    return "";
  }

  const withoutCommas = trimmedPrice.replace(/,/g, "");

  const hasCurrencySymbol = /^[^\d\s.-]/.test(withoutCommas);

  if (hasCurrencySymbol) {
    return withoutCommas;
  }

  const isOnlyNumericPrice = /^-?\d+(\.\d+)?$/.test(withoutCommas);

  if (isOnlyNumericPrice) {
    return `$${withoutCommas}`;
  }

  return withoutCommas;
};

export const PENDING_SURVEY_TOKEN_KEY =
  "eveness_public_survey_token";

export function savePendingSurveyToken(token) {
  const normalizedToken = String(token || "").trim();

  if (!normalizedToken || typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(
      PENDING_SURVEY_TOKEN_KEY,
      normalizedToken,
    );
  } catch {}

  try {
    window.sessionStorage.setItem(
      PENDING_SURVEY_TOKEN_KEY,
      normalizedToken,
    );
  } catch {}
}

export function getPendingSurveyToken() {
  if (typeof window === "undefined") {
    return "";
  }

  try {
    const localToken = window.localStorage.getItem(
      PENDING_SURVEY_TOKEN_KEY,
    );

    if (localToken) {
      return localToken;
    }
  } catch {}

  try {
    return (
      window.sessionStorage.getItem(
        PENDING_SURVEY_TOKEN_KEY,
      ) || ""
    );
  } catch {
    return "";
  }
}

export function removePendingSurveyToken() {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.removeItem(
      PENDING_SURVEY_TOKEN_KEY,
    );
  } catch {}

  try {
    window.sessionStorage.removeItem(
      PENDING_SURVEY_TOKEN_KEY,
    );
  } catch {}
}

export const parseStorePrice = (value) => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }
  const normalizedValue = String(value ?? "")
    .replace(/,/g, "")
    .replace(/[^0-9.-]/g, "");
  const parsedValue = Number.parseFloat(normalizedValue);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

export const formatStorePrice = (value) => {
  return `$${parseStorePrice(value).toFixed(2)}`;
};

export const getProductPricing = (
  product,
  isCollectionProduct = false,
) => {
  
  const originalPrice = parseStorePrice(
    product?.original_price ??
      product?.originalPrice ??
      product?.price,
  );

  const rawDiscountedPrice =
    product?.discounted_price ??
    product?.discountedPrice;

  const hasExplicitDiscountedPrice =
    rawDiscountedPrice !== null &&
    rawDiscountedPrice !== undefined &&
    rawDiscountedPrice !== "";

  const configuredDiscountPercent = Math.max(
    0,
    parseStorePrice(
      product?.discount ??
        product?.discount_percent ??
        product?.discountPercent,
    ),
  );

  let discountedPrice = originalPrice;

  if (isCollectionProduct) {
    if (hasExplicitDiscountedPrice) {
      discountedPrice = parseStorePrice(rawDiscountedPrice);
    } else if (configuredDiscountPercent > 0) {
      discountedPrice =
        originalPrice * (1 - configuredDiscountPercent / 100);
    }
  }

  const hasDiscount =
    isCollectionProduct &&
    originalPrice > 0 &&
    discountedPrice >= 0 &&
    discountedPrice < originalPrice;

  const calculatedDiscountPercent =
    hasDiscount && originalPrice > 0
      ? ((originalPrice - discountedPrice) / originalPrice) * 100
      : 0;

  const discountPercent = hasDiscount
    ? configuredDiscountPercent > 0
      ? configuredDiscountPercent
      : calculatedDiscountPercent
    : 0;

  return {
    originalPrice,
    discountedPrice: hasDiscount
      ? discountedPrice
      : originalPrice,
    discountPercent:
      Math.round(discountPercent * 100) / 100,
    savings: hasDiscount
      ? originalPrice - discountedPrice
      : 0,
    hasDiscount,
  };
};

export const toAmount = (value) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const formatPrice = (value) => toAmount(value).toFixed(2);

export const getOrderItemQuantity = (item) => Number(item?.quantity || 0);

export const getFirstAvailableAmount = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") {
      return toAmount(value);
    }
  }

  return 0;
};

export const getOrderItemLineTotal = (item, getUnitPrice) => {
  if (item?.line_total !== undefined && item?.line_total !== null) {
    return toAmount(item.line_total);
  }

  return getUnitPrice(item) * getOrderItemQuantity(item);
};

export const getOrderItemOriginalLineTotal = (item, getOriginalUnitPrice) =>
  getOriginalUnitPrice(item) * getOrderItemQuantity(item);

export const getOrderItemSavings = ({
  item,
  getOriginalUnitPrice,
  getLineTotal,
}) => {
  if (item?.savings !== undefined && item?.savings !== null) {
    return toAmount(item.savings);
  }

  const originalLineTotal =
    getOriginalUnitPrice(item) * getOrderItemQuantity(item);

  return Math.max(originalLineTotal - getLineTotal(item), 0);
};

export const hasOrderItemDiscount = (item, getSavings) =>
  toAmount(item?.discount_percent) > 0 || getSavings(item) > 0;

export const getOrderTotalAmount = (order, getLineTotal) => {
  if (order?.total_amount !== undefined && order?.total_amount !== null) {
    return toAmount(order.total_amount);
  }

  if (order?.total !== undefined && order?.total !== null) {
    return toAmount(order.total);
  }

  return (order?.items || []).reduce(
    (total, item) => total + getLineTotal(item),
    0,
  );
};

export const getOrderSavingsAmount = (order, getSavings) => {
  if (
    order?.discounted_amount !== undefined &&
    order?.discounted_amount !== null
  ) {
    return toAmount(order.discounted_amount);
  }

  return (order?.items || []).reduce(
    (total, item) => total + getSavings(item),
    0,
  );
};

export const getOrderTotalItems = (items = []) =>
  items.reduce((sum, item) => sum + getOrderItemQuantity(item), 0);